import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { AppliedCoupon } from "../coupons/models/types/couponTypes";

// Cart item type (customize as needed)
export interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  shippingCost: number;
  description?: string;
  selectedOptions?: { [key: string]: string };
  selectedColor?: string;
  productCode?: string; // Product code for invoice
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (
    id: string,
    selectedOptions?: { [key: string]: string },
    selectedColor?: string
  ) => void;
  updateQuantity: (
    id: string,
    quantity: number,
    selectedOptions?: { [key: string]: string },
    selectedColor?: string
  ) => void;
  clearCart: () => void;
  cartDrawerOpen: boolean;
  openCartDrawer: () => void;
  closeCartDrawer: () => void;
  appliedCoupon: AppliedCoupon | null;
  applyCoupon: (coupon: AppliedCoupon) => void;
  removeCoupon: () => void;
  getSubtotal: () => number;
  getDiscountAmount: () => number;
  getTotal: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "cart";
const COUPON_STORAGE_KEY = "appliedCoupon";

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(
    null
  );

  // Load cart and coupon from localStorage on mount
  useEffect(() => {
    const storedCart = localStorage.getItem(CART_STORAGE_KEY);
    const storedCoupon = localStorage.getItem(COUPON_STORAGE_KEY);

    if (storedCart) setCart(JSON.parse(storedCart));
    if (storedCoupon) setAppliedCoupon(JSON.parse(storedCoupon));
  }, []);

  // Save cart to localStorage on change
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  // Save coupon to localStorage on change
  useEffect(() => {
    if (appliedCoupon) {
      localStorage.setItem(COUPON_STORAGE_KEY, JSON.stringify(appliedCoupon));
    } else {
      localStorage.removeItem(COUPON_STORAGE_KEY);
    }
  }, [appliedCoupon]);

  const addToCart = (item: CartItem) => {
    setCart((prev) => {
      if (!Array.isArray(prev)) return [item];

      // Create a unique key for items with different options and color
      const itemKey = `${item.id}-${JSON.stringify(
        item.selectedOptions || {}
      )}-${item.selectedColor || ""}`;

      const existing = prev.find((i) => {
        const existingKey = `${i.id}-${JSON.stringify(
          i.selectedOptions || {}
        )}-${i.selectedColor || ""}`;
        return existingKey === itemKey;
      });

      if (existing) {
        return prev.map((i) => {
          const existingKey = `${i.id}-${JSON.stringify(
            i.selectedOptions || {}
          )}-${i.selectedColor || ""}`;
          return existingKey === itemKey
            ? { ...i, quantity: i.quantity + item.quantity }
            : i;
        });
      }
      return [...prev, item];
    });
  };

  const removeFromCart = (
    id: string,
    selectedOptions?: { [key: string]: string },
    selectedColor?: string
  ) => {
    setCart((prev) => {
      const itemKey = `${id}-${JSON.stringify(selectedOptions || {})}-${
        selectedColor || ""
      }`;

      return prev.filter((i) => {
        const existingKey = `${i.id}-${JSON.stringify(
          i.selectedOptions || {}
        )}-${i.selectedColor || ""}`;
        return existingKey !== itemKey;
      });
    });
  };

  const updateQuantity = (
    id: string,
    quantity: number,
    selectedOptions?: { [key: string]: string },
    selectedColor?: string
  ) => {
    setCart((prev) => {
      const itemKey = `${id}-${JSON.stringify(selectedOptions || {})}-${
        selectedColor || ""
      }`;

      return prev.map((i) => {
        const existingKey = `${i.id}-${JSON.stringify(
          i.selectedOptions || {}
        )}-${i.selectedColor || ""}`;
        return existingKey === itemKey ? { ...i, quantity } : i;
      });
    });
  };

  const clearCart = () => {
    setCart([]);
    setAppliedCoupon(null);
  };

  const openCartDrawer = () => setCartDrawerOpen(true);
  const closeCartDrawer = () => setCartDrawerOpen(false);

  const applyCoupon = (coupon: AppliedCoupon) => {
    setAppliedCoupon(coupon);
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
  };

  const getSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const getDiscountAmount = () => {
    if (!appliedCoupon) return 0;
    const subtotal = getSubtotal();
    return (subtotal * appliedCoupon.discountPercentage) / 100;
  };

  const getTotal = () => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    // Shipping cost is now selected in checkout, not from products
    return subtotal - discount;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartDrawerOpen,
        openCartDrawer,
        closeCartDrawer,
        appliedCoupon,
        applyCoupon,
        removeCoupon,
        getSubtotal,
        getDiscountAmount,
        getTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
};
